// Fire
// Features:
// - CPU-based particle system for fire effect
// - Poisson emission scheduling for natural randomness
// - Physics simulation with drag and attractor forces
// - Custom shader for particle rendering

import { useEffect, useMemo, useRef } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { useTexture } from '@react-three/drei'
import * as THREE from 'three'

import vertexShader from '../shaders/fire/vertex.glsl'
import fragmentShader from '../shaders/fire/fragment.glsl'

import smokeTextureURL from '/textures/smoke.png'

const FIRE_CONFIG = {
    maxParticles: 100,
    emissionRate: 5,
    lifetime: 1.5,
    startSpeed: 0.1,
    spread: 0.2,
    drag: 0.1,
    spinSpeed: Math.PI / 8,
    position: new THREE.Vector3(-2.17, 0.61, -3.03),
    colorStart: new THREE.Color('#ff5600'),
    colorEnd: new THREE.Color('#ff0000'),
    attractorOffset: 0.05,
    attractorIntensity: 0.2,
    attractorRadius: 4.7,
    sizeStart: 0.42,
    sizeEnd: 0.32,
}

export default function Fire() {
    const { gl, size, camera } = useThree()
    const particlesRef = useRef([])
    const nextEmitAtRef = useRef(0)

    // Textures
    const fireTexture = useTexture(smokeTextureURL, (t) => {
        t.flipY = false
        t.colorSpace = THREE.SRGBColorSpace
    })

    // Geometry & Material
    const { geometry, material } = useMemo(() => {
        const geom = new THREE.BufferGeometry()
        const positions = new Float32Array(FIRE_CONFIG.maxParticles * 3)
        const particleData = new Float32Array(FIRE_CONFIG.maxParticles * 2) // life, id

        geom.setAttribute(
            'position',
            new THREE.Float32BufferAttribute(positions, 3)
        )
        geom.setAttribute(
            'particleData',
            new THREE.Float32BufferAttribute(particleData, 2)
        )

        geom.attributes.position.setUsage(THREE.DynamicDrawUsage)
        geom.attributes.particleData.setUsage(THREE.DynamicDrawUsage)
        geom.boundingSphere = new THREE.Sphere(new THREE.Vector3(), 1000)

        const mat = new THREE.ShaderMaterial({
            uniforms: {
                time: { value: 0 },
                spinSpeed: { value: FIRE_CONFIG.spinSpeed },
                map: { value: fireTexture },
                sizeStart: { value: FIRE_CONFIG.sizeStart },
                sizeEnd: { value: FIRE_CONFIG.sizeEnd },
                colourStart: { value: FIRE_CONFIG.colorStart },
                colourEnd: { value: FIRE_CONFIG.colorEnd },
                uProjectionScale: { value: 1.0 },
            },
            vertexShader,
            fragmentShader,
            transparent: true,
            depthWrite: false,
            depthTest: true,
            blending: THREE.CustomBlending,
            blendEquation: THREE.AddEquation,
            blendSrc: THREE.OneFactor,
            blendDst: THREE.OneMinusSrcAlphaFactor,
        })

        return { geometry: geom, material: mat }
    }, [fireTexture])

    // Particle Spawning Logic
    const spawnParticle = () => {
        // Spherical spread
        const phi = Math.random() * Math.PI * 2
        const theta = Math.random() * Math.PI
        const r = Math.random() * FIRE_CONFIG.spread

        const dir = new THREE.Vector3()
        dir.setFromSphericalCoords(r, theta, phi)

        // Upward velocity with variance
        const velPhi = Math.random() * Math.PI * 2
        const velTheta = Math.random() * (Math.PI / 10)

        const vdir = new THREE.Vector3()
        vdir.setFromSphericalCoords(FIRE_CONFIG.startSpeed, velTheta, velPhi)

        return {
            position: new THREE.Vector3().copy(FIRE_CONFIG.position).add(dir),
            velocity: vdir,
            life: 0,
            maxLife: FIRE_CONFIG.lifetime,
            id: Math.random(),
        }
    }

    // Handle tab visibility to reset emission
    useEffect(() => {
        const onVisibilityChange = () => {
            nextEmitAtRef.current = 0
        }
        document.addEventListener('visibilitychange', onVisibilityChange)
        return () =>
            document.removeEventListener('visibilitychange', onVisibilityChange)
    }, [])

    // Simulation Loop
    useFrame((state, rawDelta) => {
        // Cap delta to prevent huge jumps
        const delta = rawDelta > 0.25 ? 0.016 : rawDelta
        const particles = particlesRef.current

        // 1. Emission (Poisson process)
        nextEmitAtRef.current -= delta
        const mean = 1 / Math.max(1, FIRE_CONFIG.emissionRate)

        while (
            nextEmitAtRef.current <= 0 &&
            particles.length < FIRE_CONFIG.maxParticles
        ) {
            particles.push(spawnParticle())
            const u = Math.max(1e-6, Math.random())
            const interval = -Math.log(u) * mean
            nextEmitAtRef.current += Math.max(0.0005, interval)
        }

        // 2. Physics Update
        const attractorTarget = new THREE.Vector3(
            FIRE_CONFIG.position.x,
            FIRE_CONFIG.position.y + FIRE_CONFIG.attractorOffset,
            FIRE_CONFIG.position.z
        )
        const attractorRadiusSq =
            Math.max(0.001, FIRE_CONFIG.attractorRadius) ** 2

        for (let i = 0; i < particles.length; i++) {
            const p = particles[i]
            p.life = Math.min(p.maxLife, p.life + delta)

            // Drag
            p.velocity.addScaledVector(p.velocity, -FIRE_CONFIG.drag * delta)

            // Attractor force
            const dir = new THREE.Vector3().subVectors(
                attractorTarget,
                p.position
            )
            const distSq = dir.lengthSq()

            if (distSq > 1e-8) {
                const dist = Math.sqrt(distSq)
                dir.multiplyScalar(1 / dist) // normalize
                const k =
                    FIRE_CONFIG.attractorIntensity /
                    (1 + distSq / attractorRadiusSq)
                p.velocity.addScaledVector(dir, k * delta)
            }

            // Integrate
            p.position.addScaledVector(p.velocity, delta)
        }

        // 3. Cull dead particles
        for (let i = particles.length - 1; i >= 0; i--) {
            if (particles[i].life >= particles[i].maxLife) {
                particles.splice(i, 1)
            }
        }

        // 4. Update Geometry
        const posArray = geometry.attributes.position.array
        const dataArray = geometry.attributes.particleData.array

        for (let i = 0; i < particles.length; i++) {
            const p = particles[i]
            const i3 = i * 3
            const i2 = i * 2

            posArray[i3] = p.position.x
            posArray[i3 + 1] = p.position.y
            posArray[i3 + 2] = p.position.z

            dataArray[i2] = p.life / p.maxLife
            dataArray[i2 + 1] = p.id
        }

        geometry.setDrawRange(0, particles.length)
        geometry.attributes.position.needsUpdate = true
        geometry.attributes.particleData.needsUpdate = true

        // 5. Update Uniforms
        material.uniforms.time.value = state.clock.elapsedTime

        // Projection scale for consistent sizing
        const dpr = Math.min(gl.getPixelRatio(), 2)
        const fovRad = THREE.MathUtils.degToRad(camera.fov * 0.5)
        material.uniforms.uProjectionScale.value =
            (size.height * dpr) / (2 * Math.tan(fovRad))
    })

    return <points geometry={geometry} material={material} />
}
